import styled, { css, keyframes } from "styled-components";

export const Backdrop = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.82);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9999;
`;

export const Shell = styled.div`
  background: ${({ theme }) => theme.colors.grey850};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  max-width: 1320px;
  overflow: hidden;
  width: 100%;
`;

export const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const LoadingShell = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: center;
  min-height: 320px;
  padding: 48px;
`;

export const LoadingSpinner = styled.div`
  animation: ${spin} 0.8s linear infinite;
  border: 3px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  border-top-color: ${({ theme }) => theme.colors.envy};
  height: 36px;
  width: 36px;
`;

export const LoadingText = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  font-weight: 600;
`;

export const Header = styled.div`
  background: ${({ theme }) => theme.colors.grey850};
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 9px;
  overflow: hidden;
  padding: 13px 18px;
  position: relative;
`;

export const HeaderBg = styled.div<{ $bg?: string }>`
  inset: 0;
  position: absolute;

  ${({ $bg }) =>
    $bg &&
    css`
      background-image: url(${$bg});
      background-position: center;
      background-size: cover;
      filter: blur(6px);
      opacity: 0.6;
      transform: scale(1.12);
    `}
`;

export const HeaderScrim = styled.div`
  background: linear-gradient(
    180deg,
    rgba(20, 30, 38, 0.55) 0%,
    rgba(20, 30, 38, 0.88) 100%
  );
  inset: 0;
  position: absolute;
`;

export const HeaderTop = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  position: relative;
  z-index: 1;

  @media (max-width: 760px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
`;

export const IdentityRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  min-width: 0;
`;

export const MapIcon = styled.img`
  border-radius: 6px;
  flex-shrink: 0;
  height: 34px;
  object-fit: cover;
  width: 34px;
`;

export const EyebrowRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const Eyebrow = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 18px;
  font-weight: 800;
  gap: 7px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const BetaTag = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  padding: 2px 7px;
  text-transform: uppercase;
`;

export const FeedbackWrap = styled.div`
  & > button {
    font-size: 14px;
    gap: 6px;
    height: 36px;
    justify-content: center;
    padding: 0 14px;
    width: 100%;
  }
`;

export const MapName = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 26px;
  font-weight: 700;
  line-height: 1.1;
`;

export const HeaderRight = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 8px;
  padding-right: 40px;

  @media (max-width: 760px) {
    flex-direction: row;
    padding-right: 0;
  }
`;

export const MetaRow = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  font-weight: 600;
  min-width: 0;
`;

export const HeaderDesc = styled.p`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  margin: 3px 0 0;
  max-width: 520px;
  opacity: 0.92;
`;

export const ResultBadge = styled.span<{ $variant: "win" | "tie" | "loss" }>`
  border-radius: 5px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  text-transform: uppercase;

  ${({ $variant, theme }) =>
    $variant === "win"
      ? css`
          background: ${theme.colors.envy};
          color: ${theme.colors.ice};
        `
      : $variant === "tie"
      ? css`
          background: ${theme.colors.chalkAlpha20};
          color: ${theme.colors.ice};
        `
      : css`
          background: ${theme.colors.alert || "#e0524d"};
          color: ${theme.colors.ice};
        `}
`;

export const Score = styled.span<{ $variant: "win" | "tie" | "loss" }>`
  color: ${({ $variant, theme }) =>
    $variant === "win"
      ? theme.colors.envy
      : $variant === "loss"
      ? theme.colors.alert || "#e0524d"
      : theme.colors.ice};
  font-size: 24px;
  font-weight: 800;
`;

export const DownloadLink = styled.a`
  align-items: center;
  background: ${({ theme }) => theme.colors.chalkAlpha05};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 7px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  height: 36px;
  justify-content: center;
  padding: 0 14px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha10};
  }
`;

export const CloseButton = styled.button`
  align-items: center;
  backdrop-filter: blur(4px);
  background: rgba(7, 9, 12, 0.55);
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: background 0.15s ease;
  width: 32px;
  z-index: 2;

  &:hover {
    background: rgba(7, 9, 12, 0.8);
    border-color: ${({ theme }) => theme.colors.chalkAlpha30};
  }
`;

export const Body = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;

  @media (max-width: 940px) {
    flex-direction: column;
  }
`;

export const Stage = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.grey850};
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0;
  padding: 12px 14px;
`;

export const PlayerWrap = styled.div`
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  width: 100%;

  .vjs-progress-control {
    display: none !important;
  }

  .vjs-control-bar {
    display: flex;
    align-items: center;
  }
  .vjs-remaining-time {
    margin-left: auto;
    order: 90;
  }
  .vjs-picture-in-picture-control {
    order: 98;
  }
  .vjs-fullscreen-control {
    order: 99;
  }
`;

export const Seeker = styled.div`
  bottom: 44px;
  cursor: pointer;
  height: 22px;
  left: 14px;
  position: absolute;
  right: 14px;
  z-index: 2;
`;

export const Track = styled.div`
  background: rgba(255, 255, 255, 0.22);
  border-radius: 3px;
  bottom: 0;
  height: 5px;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
`;

export const Fill = styled.div`
  background: rgba(255, 255, 255, 0.4);
  bottom: 0;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
`;

export const MomentBand = styled.div<{ $best?: boolean }>`
  background: ${({ $best, theme }) =>
    $best ? theme.colors.bestMoment : theme.colors.envy};
  bottom: 0;
  height: 100%;
  opacity: ${({ $best }) => ($best ? 0.95 : 0.8)};
  position: absolute;
  top: 0;
  z-index: 1;
`;

export const Divider = styled.span`
  background: rgba(7, 9, 12, 0.85);
  bottom: -1px;
  height: 7px;
  margin-left: -1px;
  position: absolute;
  width: 2px;
  z-index: 2;
`;

export const Segment = styled.div<{ $hasMoments?: boolean }>`
  bottom: 0;
  cursor: pointer;
  position: absolute;
  top: 0;
  z-index: 3;

  &:hover {
    background: ${({ $hasMoments }) =>
      $hasMoments ? "rgba(77, 189, 148, 0.22)" : "transparent"};
  }
`;

export const Playhead = styled.div`
  background: ${({ theme }) => theme.colors.ice};
  border-radius: 50%;
  bottom: -2.5px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.9);
  height: 13px;
  margin-left: -6.5px;
  position: absolute;
  pointer-events: none;
  width: 13px;
  z-index: 3;
`;

export const RoundTip = styled.div`
  backdrop-filter: blur(12px);
  background: rgba(37, 58, 73, 0.72);
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 9px;
  bottom: 28px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(380px, 80vw);
  padding: 10px 12px;
  pointer-events: none;
  position: absolute;
  transform: translateX(-50%);
  width: max-content;
  z-index: 5;
`;

export const TipMoment = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TipTop = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const TipRound = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const TipTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  font-weight: 700;
`;

export const TipBest = styled.span`
  color: ${({ theme }) => theme.colors.bestMoment};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const TipEmpty = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 500;
`;

export const flashKeyframes = keyframes`
  0% { opacity: 0; transform: translateY(-10px); }
  7% { opacity: 1; transform: translateY(0); }
  85% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

export const FlashOverlay = styled.div`
  align-items: flex-end;
  animation: ${flashKeyframes} 4.5s ease forwards;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 72%;
  pointer-events: none;
  position: absolute;
  right: 20px;
  text-align: right;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.9);
  top: 18px;
  z-index: 3;
`;

export const FlashRound = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const FlashTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 28px;
  font-weight: 800;
  line-height: 1.05;
`;

export const SeekerKey = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  justify-content: center;
  margin-top: 14px;
`;

export const KeyItem = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 7px;
`;

export const KeyDot = styled.span<{ $variant: "best" | "highlight" }>`
  background: ${({ $variant, theme }) =>
    $variant === "best" ? theme.colors.bestMoment : theme.colors.envy};
  border-radius: 50%;
  height: 10px;
  width: 10px;
`;

export const KeyHint = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
`;

export const Rail = styled.div`
  background: ${({ theme }) => theme.colors.grey850};
  border-left: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  flex-shrink: 0;
  position: relative;
  width: 360px;

  @media (max-width: 940px) {
    border-left: none;
    border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
    height: 40vh;
    width: 100%;
  }
`;

export const RailInner = styled.div`
  display: flex;
  flex-direction: column;
  inset: 0;
  min-height: 0;
  position: absolute;
`;

export const RailHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  padding: 14px 16px 4px;
`;

export const RailTitle = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

export const RailCount = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  padding: 1px 9px;
`;

export const RailSub = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  padding: 0 16px 12px;
`;

export const RailList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
`;

export const RailEmpty = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  padding: 16px;
  text-align: center;
`;
